import React, { useState, useMemo, useEffect } from 'react';
import type {
  TimetableDto,
  TimetableConflict,
  SubjectDto,
  UserDto,
  CreateTimetablePayload,
  SchoolClassDto,
  SchoolCode,
} from '../../api/axios';
import { formatClassLevelLabel } from '../../utils/classLevelLabel';
import TimetableCard from './TimetableCard';
import type { SubjectColor } from './TimetableCard';
import TimetableToolbar from './TimetableToolbar';
import { CreateModal, EditModal } from './TimetableModals';
import styles from './TimetableGrid.module.css';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;
const DAY_LABELS: Record<string, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
};
const DAY_SHORT: Record<string, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
};

interface PeriodSlot {
  period: number;
  startTime: string;
  endTime: string;
}

interface TimetableScheduleConfig {
  periods: PeriodSlot[];
  breakLabel: string;
  breakTime: string;
  lunchLabel: string;
  lunchTime: string;
}

const DEFAULT_SCHEDULE_CONFIG: TimetableScheduleConfig = {
  periods: [
    { period: 1, startTime: '07:30', endTime: '08:10' },
    { period: 2, startTime: '08:10', endTime: '08:50' },
    { period: 3, startTime: '08:50', endTime: '09:30' },
    { period: 4, startTime: '09:30', endTime: '10:10' },
    { period: 5, startTime: '10:40', endTime: '11:20' },
    { period: 6, startTime: '11:20', endTime: '12:00' },
    { period: 7, startTime: '13:30', endTime: '14:10' },
    { period: 8, startTime: '14:10', endTime: '14:50' },
    { period: 9, startTime: '14:50', endTime: '15:30' },
  ],
  breakLabel: 'Morning break',
  breakTime: '20 min',
  lunchLabel: 'Lunch break',
  lunchTime: '45 min',
};

type DisplayRow = { type: 'period'; slot: PeriodSlot } | { type: 'break' | 'lunch' };

const buildDisplayRows = (periods: PeriodSlot[]): DisplayRow[] => [
  ...periods.filter((p) => p.period <= 4).map(s => ({ type: 'period' as const, slot: s })),
  { type: 'break' },
  ...periods.filter((p) => p.period >= 5 && p.period <= 6).map(s => ({ type: 'period' as const, slot: s })),
  { type: 'lunch' },
  ...periods.filter((p) => p.period >= 7).map(s => ({ type: 'period' as const, slot: s })),
];

/* Color helper — same palette as the old timetable */
const SUBJECT_COLOR_MAP: Record<string, SubjectColor> = {
  'english': { bg: '#fce4ec', border: '#f48fb1', text: '#880e4f' },
  'math': { bg: '#e3f2fd', border: '#64b5f6', text: '#0d47a1' },
  'mathematics': { bg: '#e3f2fd', border: '#64b5f6', text: '#0d47a1' },
  'physics': { bg: '#fff3e0', border: '#ffb74d', text: '#e65100' },
  'chemistry': { bg: '#e8f5e9', border: '#81c784', text: '#1b5e20' },
  'biology': { bg: '#f1f8e9', border: '#aed581', text: '#33691e' },
  'chichewa': { bg: '#f3e5f5', border: '#ba68c8', text: '#4a148c' },
  'history': { bg: '#ffebee', border: '#e57373', text: '#b71c1c' },
  'geography': { bg: '#e0f2f1', border: '#4db6ac', text: '#004d40' },
  'computer': { bg: '#e1bee7', border: '#ab47bc', text: '#4a148c' },
  'computer science': { bg: '#e1bee7', border: '#ab47bc', text: '#4a148c' },
  'art': { bg: '#fff9c4', border: '#fff176', text: '#f57f17' },
  'music': { bg: '#ffe0b2', border: '#ffb74d', text: '#e65100' },
  'pe': { bg: '#c8e6c9', border: '#66bb6a', text: '#1b5e20' },
  'physical education': { bg: '#c8e6c9', border: '#66bb6a', text: '#1b5e20' },
  'science': { bg: '#e0f7fa', border: '#4dd0e1', text: '#006064' },
};

function getSubjectColor(subjectName: string | undefined): SubjectColor {
  if (!subjectName) return { bg: '#f5f5f5', border: '#ddd', text: '#666' };
  const key = subjectName.toLowerCase().trim();
  if (SUBJECT_COLOR_MAP[key]) return SUBJECT_COLOR_MAP[key];
  const hash = subjectName.split('').reduce((acc, c) => c.charCodeAt(0) + ((acc << 5) - acc), 0);
  const hue = Math.abs(hash) % 360;
  const sat = 40 + (Math.abs(hash) % 20);
  const lit = 88 + (Math.abs(hash) % 7);
  return {
    bg: `hsl(${hue}, ${sat}%, ${lit}%)`,
    border: `hsl(${hue}, ${sat}%, ${lit - 18}%)`,
    text: `hsl(${hue}, ${sat + 20}%, ${lit - 52}%)`,
  };
}

function todayDayName(): string {
  return ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][new Date().getDay()];
}

/* ===== Props ===== */

export interface TimetableGridProps {
  timetables: TimetableDto[];
  conflictMessages: Record<number, TimetableConflict[]>;
  loading: boolean;
  viewMode: 'student' | 'teacher';
  /** Used for class-level labels (Form 1–4 vs Std 1–8). */
  school?: SchoolCode;
  activeForm: number;
  selectedTeacherId: number | null;
  classes: SchoolClassDto[];
  subjects: SubjectDto[];
  /**
   * When provided (including empty array), create/edit modals use exactly this list for subjects.
   * When omitted, modals use all `subjects` (legacy behaviour).
   */
  linkSubjects?: SubjectDto[];
  /** Mapping of subject_id -> linked teacher_id for the active class context. */
  linkedTeacherBySubjectId?: Record<number, number>;
  teacherAccounts: UserDto[];
  publishing: boolean;
  generating: boolean;
  clearing: boolean;
  onViewModeChange: (mode: 'student' | 'teacher') => void;
  onFormChange: (form: number) => void;
  onTeacherChange: (id: number | null) => void;
  onPublish: () => void;
  onGenerate: () => void;
  onClear: () => void;
  onCreateEntry: (payload: CreateTimetablePayload) => Promise<void>;
  onUpdateEntry: (id: number, updates: Partial<TimetableDto>) => Promise<void>;
  onDeleteEntry: (id: number) => Promise<void>;
}

const TimetableGridComponent: React.FC<TimetableGridProps> = (props) => {
  const {
    timetables,
    conflictMessages,
    loading,
    viewMode,
    school = 'KBA',
    activeForm,
    selectedTeacherId,
    classes,
    subjects,
    linkSubjects,
    linkedTeacherBySubjectId = {},
    teacherAccounts,
    publishing,
    generating,
    clearing,
    onViewModeChange,
    onFormChange,
    onTeacherChange,
    onPublish,
    onGenerate,
    onClear,
    onCreateEntry,
    onUpdateEntry,
    onDeleteEntry,
  } = props;

  const modalSubjects = useMemo(() => {
    if (linkSubjects !== undefined) {
      return linkSubjects;
    }
    return subjects;
  }, [linkSubjects, subjects]);

  const readOnly = viewMode === 'teacher';

  /* local UI state */
  const [mobileDay, setMobileDay] = useState<string>(
    DAYS.includes(todayDayName() as any) ? todayDayName() : 'monday'
  );
  const [draggedEntry, setDraggedEntry] = useState<TimetableDto | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<{ day: string; period: number } | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createSlot, setCreateSlot] = useState<{ day: string; period: number } | null>(null);
  const [newEntryData, setNewEntryData] = useState<Partial<CreateTimetablePayload>>({ subject_id: null, teacher_id: null, room: '' });
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleConfig, setScheduleConfig] = useState<TimetableScheduleConfig>(DEFAULT_SCHEDULE_CONFIG);
  const [scheduleDraft, setScheduleDraft] = useState<TimetableScheduleConfig>(DEFAULT_SCHEDULE_CONFIG);

  const [editingEntry, setEditingEntry] = useState<TimetableDto | null>(null);
  const [editDraft, setEditDraft] = useState<{ subject_id: number | null; teacher_id: number | null; room: string }>({ subject_id: null, teacher_id: null, room: '' });

  const getLinkedTeacherId = (subjectId: number | null | undefined): number | null => {
    if (!subjectId) return null;
    return linkedTeacherBySubjectId[subjectId] ?? null;
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem('timetableScheduleConfig');
      if (raw) {
        const parsed = JSON.parse(raw) as TimetableScheduleConfig;
        if (Array.isArray(parsed.periods) && parsed.periods.length === 9) {
          setScheduleConfig(parsed);
          setScheduleDraft(parsed);
        }
      }
    } catch {
      // Fall back to defaults if parse fails
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('timetableScheduleConfig', JSON.stringify(scheduleConfig));
  }, [scheduleConfig]);

  const periodTimes = useMemo(() => {
    return scheduleConfig.periods.reduce<Record<number, { start: string; end: string }>>((acc, slot) => {
      acc[slot.period] = { start: slot.startTime, end: slot.endTime };
      return acc;
    }, {});
  }, [scheduleConfig.periods]);

  const displayRows = useMemo(() => buildDisplayRows(scheduleConfig.periods), [scheduleConfig.periods]);

  /* lookup map */
  const timetableMap = useMemo(() => {
    const map: Record<string, Record<number, TimetableDto>> = {};
    DAYS.forEach(d => { map[d] = {}; });
    timetables.forEach(tt => {
      if (tt.day && tt.period) map[tt.day] = { ...map[tt.day], [tt.period]: tt };
    });
    return map;
  }, [timetables]);

  const getEntry = (day: string, period: number) => timetableMap[day]?.[period] ?? null;

  const selectedTeacher = teacherAccounts.find(t => t.id === selectedTeacherId);

  /* ---- create ---- */
  const openCreate = (day: string, period: number) => {
    if (readOnly) return;
    setCreateSlot({ day, period });
    setNewEntryData({ subject_id: null, teacher_id: null, room: '' });
    setShowCreateModal(true);
  };

  const handleCreateDataChange = (data: Partial<CreateTimetablePayload>) => {
    setNewEntryData((prev) => {
      const next = { ...prev, ...data };
      if (data.subject_id !== undefined && data.subject_id !== prev.subject_id) {
        const linkedTeacherId = getLinkedTeacherId(data.subject_id ?? null);
        if (linkedTeacherId) {
          next.teacher_id = linkedTeacherId;
        }
      }
      return next;
    });
  };

  const handleSaveNew = async () => {
    if (!createSlot || !newEntryData.subject_id || !newEntryData.teacher_id) {
      alert('Please select both a subject and a teacher');
      return;
    }
    const time = periodTimes[createSlot.period];
    const payload: CreateTimetablePayload = {
      form: activeForm,
      day: createSlot.day as any,
      period: createSlot.period,
      subject_id: newEntryData.subject_id!,
      teacher_id: newEntryData.teacher_id!,
      room: newEntryData.room || null,
      start_time: time?.start || null,
      end_time: time?.end || null,
      status: 'draft',
    };
    try {
      await onCreateEntry(payload);
      setShowCreateModal(false);
      setCreateSlot(null);
    } catch {
      /* parent handles error */
    }
  };

  /* ---- edit ---- */
  const openEdit = (entry: TimetableDto) => {
    setEditingEntry(entry);
    setEditDraft({
      subject_id: entry.subject_id ?? null,
      teacher_id: entry.teacher_id ?? null,
      room: entry.room || '',
    });
  };

  const handleEditDraftChange = (draft: { subject_id: number | null; teacher_id: number | null; room: string }) => {
    setEditDraft((prev) => {
      const next = { ...draft };
      if (draft.subject_id !== prev.subject_id) {
        const linkedTeacherId = getLinkedTeacherId(draft.subject_id);
        if (linkedTeacherId) {
          next.teacher_id = linkedTeacherId;
        }
      }
      return next;
    });
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    await onUpdateEntry(editingEntry.id, {
      subject_id: editDraft.subject_id,
      teacher_id: editDraft.teacher_id,
      room: editDraft.room,
    } as any);
    setEditingEntry(null);
  };

  const handleDeleteEdit = async () => {
    if (!editingEntry) return;
    if (!window.confirm('Delete this timetable entry?')) return;
    await onDeleteEntry(editingEntry.id);
    setEditingEntry(null);
  };

  const openScheduleEditor = () => {
    setScheduleDraft(scheduleConfig);
    setShowScheduleModal(true);
  };

  const updateDraftPeriod = (period: number, field: 'startTime' | 'endTime', value: string) => {
    setScheduleDraft((prev) => ({
      ...prev,
      periods: prev.periods.map((slot) => (slot.period === period ? { ...slot, [field]: value } : slot)),
    }));
  };

  const handleSaveSchedule = () => {
    setScheduleConfig(scheduleDraft);
    setShowScheduleModal(false);
  };

  /* ---- drag & drop ---- */
  const handleDragStart = (e: React.DragEvent, entry: TimetableDto) => {
    setDraggedEntry(entry);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', entry.id.toString());
  };

  const handleDragOver = (e: React.DragEvent, day: string, period: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlot({ day, period });
  };

  const handleDragLeave = () => setDragOverSlot(null);

  const handleDrop = async (e: React.DragEvent, targetDay: string, targetPeriod: number) => {
    e.preventDefault();
    setDragOverSlot(null);
    if (!draggedEntry) return;
    if (draggedEntry.day === targetDay && draggedEntry.period === targetPeriod) return;

    const targetEntry = getEntry(targetDay, targetPeriod);
    if (targetEntry && targetEntry.id !== draggedEntry.id) {
      if (!window.confirm('This slot already has an entry. Swap them?')) return;
      await onUpdateEntry(targetEntry.id, { day: draggedEntry.day as any, period: draggedEntry.period } as any);
    }
    await onUpdateEntry(draggedEntry.id, { day: targetDay as any, period: targetPeriod } as any);
    setDraggedEntry(null);
  };

  const handleDragEnd = () => {
    setDraggedEntry(null);
    setDragOverSlot(null);
  };

  /* ---- render helpers ---- */
  const renderCard = (entry: TimetableDto) => (
    <TimetableCard
      key={entry.id}
      entry={entry}
      conflicts={conflictMessages[entry.id] || []}
      isDragging={draggedEntry?.id === entry.id}
      readOnly={readOnly}
      school={school}
      getSubjectColor={getSubjectColor}
      onClick={() => openEdit(entry)}
      onDragStart={(e) => handleDragStart(e, entry)}
      onDragEnd={handleDragEnd}
    />
  );

  const renderEmpty = (day: string, period: number) => {
    const isDragTarget = dragOverSlot?.day === day && dragOverSlot?.period === period;
    return (
      <div
        className={`${styles.emptySlot} ${isDragTarget ? styles.dragOver : ''}`}
        onClick={() => openCreate(day, period)}
        onDragOver={e => handleDragOver(e, day, period)}
        onDragLeave={handleDragLeave}
        onDrop={e => handleDrop(e, day, period)}
      >
        {!readOnly && <span className={styles.plusIcon}>+</span>}
      </div>
    );
  };

  const renderSeparator = (label: string, time: string) => (
    <div className={styles.separator}>
      <span className={styles.separatorLine} />
      <span>{label} &middot; {time}</span>
      <span className={styles.separatorLine} />
    </div>
  );

  /* ---- title ---- */
  const titleText = viewMode === 'student'
    ? `${formatClassLevelLabel(school, activeForm)} Timetable`
    : selectedTeacher
      ? `${selectedTeacher.name}'s Timetable`
      : 'Select a Teacher';

  const today = todayDayName();

  /* ===== JSX ===== */
  return (
    <div style={{ padding: '20px 16px', flex: 1, background: '#f5f7fa', minHeight: '100%' }}>
      <h2 style={{ margin: '0 0 16px', fontSize: 22, fontWeight: 700, color: '#1f2937' }}>
        Timetable Management
      </h2>

      <TimetableToolbar
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        classes={classes}
        activeForm={activeForm}
        onFormChange={onFormChange}
        teacherAccounts={teacherAccounts}
        selectedTeacherId={selectedTeacherId}
        onTeacherChange={onTeacherChange}
        onPublish={onPublish}
        onGenerate={onGenerate}
        onClear={onClear}
        publishing={publishing}
        generating={generating}
        clearing={clearing}
      />

      <div className={styles.titleBar}>
        {titleText}
        <button
          className={styles.timeSettingsBtn}
          onClick={openScheduleEditor}
          type="button"
        >
          Edit Times
        </button>
      </div>

      {loading ? (
        <div className={styles.loadingState}>Loading timetable...</div>
      ) : viewMode === 'teacher' && !selectedTeacherId ? (
        <div className={styles.emptyState}>Please select a teacher from the dropdown above to view their timetable.</div>
      ) : (
        <>
          {/* Mobile day tabs */}
          <div className={styles.dayTabs}>
            {DAYS.map(d => (
              <button
                key={d}
                className={`${styles.dayTab} ${mobileDay === d ? styles.active : ''}`}
                onClick={() => setMobileDay(d)}
              >
                {d === today ? `${DAY_SHORT[d]} (Today)` : DAY_SHORT[d]}
              </button>
            ))}
          </div>

          <div className={styles.gridWrapper}>
            {/* ===== Desktop grid ===== */}
            <div className={styles.grid}>
              {/* Header */}
              <div className={styles.gridHeader}>
                <div className={styles.gridHeaderCorner}>Time</div>
                {DAYS.map(d => (
                  <div key={d} className={styles.gridHeaderDay}>
                    {DAY_LABELS[d]}
                    {d === today && <span className={styles.todayDot} />}
                  </div>
                ))}
              </div>

              {/* Rows */}
              {displayRows.map((row) => {
                if (row.type === 'break') {
                  return <div key="break" className={styles.gridSeparator}><span className={styles.separatorLine}/><span>{scheduleConfig.breakLabel} &middot; {scheduleConfig.breakTime}</span><span className={styles.separatorLine}/></div>;
                }
                if (row.type === 'lunch') {
                  return <div key="lunch" className={styles.gridSeparator}><span className={styles.separatorLine}/><span>{scheduleConfig.lunchLabel} &middot; {scheduleConfig.lunchTime}</span><span className={styles.separatorLine}/></div>;
                }

                if (row.type !== 'period') return null;
                const slot = row.slot;
                return (
                  <div key={`row-${slot.period}`} className={styles.gridRow}>
                    <div className={styles.gridTimeCell}>
                      <span className={styles.time}>{slot.startTime}</span>
                      <span className={styles.periodNum}>P{slot.period}</span>
                    </div>
                    {DAYS.map(day => {
                      const entry = getEntry(day, slot.period);
                      return (
                        <div
                          key={`${day}-${slot.period}`}
                          className={styles.gridCell}
                          onDragOver={!entry ? (e) => handleDragOver(e, day, slot.period) : undefined}
                          onDragLeave={!entry ? handleDragLeave : undefined}
                          onDrop={!entry ? (e) => handleDrop(e, day, slot.period) : undefined}
                        >
                          {entry ? renderCard(entry) : renderEmpty(day, slot.period)}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {/* ===== Mobile list ===== */}
            <div className={styles.mobileList}>
              {displayRows.map((row) => {
                if (row.type === 'break') return <React.Fragment key="m-break">{renderSeparator(scheduleConfig.breakLabel, scheduleConfig.breakTime)}</React.Fragment>;
                if (row.type === 'lunch') return <React.Fragment key="m-lunch">{renderSeparator(scheduleConfig.lunchLabel, scheduleConfig.lunchTime)}</React.Fragment>;

                if (row.type !== 'period') return null;
                const slot = row.slot;
                const entry = getEntry(mobileDay, slot.period);

                return (
                  <div key={`m-${slot.period}`} className={styles.mobileRow}>
                    <div className={styles.mobileTimeLabel}>
                      <span className={styles.time}>{slot.startTime}</span>
                      <span className={styles.periodNum}>P{slot.period}</span>
                    </div>
                    <div className={styles.mobileCardArea}>
                      {entry ? renderCard(entry) : renderEmpty(mobileDay, slot.period)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Conflicts */}
          {Object.keys(conflictMessages).length > 0 && (
            <div className={styles.conflictsPanel}>
              <h4>Conflict Warnings</h4>
              {Object.entries(conflictMessages).map(([id, conflicts]) =>
                conflicts.map((c, i) => (
                  <div key={`${id}-${i}`} className={styles.conflictItem}>
                    <strong>Entry #{id}:</strong> {c.message}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateModal
          slot={createSlot}
          data={newEntryData}
          subjects={modalSubjects}
          teachers={teacherAccounts}
          onDataChange={handleCreateDataChange}
          onSave={handleSaveNew}
          onClose={() => { setShowCreateModal(false); setCreateSlot(null); }}
        />
      )}

      {editingEntry && (
        <EditModal
          entry={editingEntry}
          draft={editDraft}
          subjects={modalSubjects}
          teachers={teacherAccounts}
          onDraftChange={handleEditDraftChange}
          onSave={handleSaveEdit}
          onDelete={handleDeleteEdit}
          onClose={() => setEditingEntry(null)}
        />
      )}

      {showScheduleModal && (
        <div className={styles.modalOverlay} onClick={() => setShowScheduleModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHandle} />
            <div className={styles.modalTitle}>Edit Schedule Times</div>
            <div className={styles.modalSubtitle}>Adjust period side-times and break/lunch labels.</div>

            {scheduleDraft.periods.map((slot) => (
              <div key={slot.period} className={styles.timeEditorRow}>
                <div className={styles.timeEditorPeriod}>P{slot.period}</div>
                <input
                  type="time"
                  value={slot.startTime}
                  onChange={(e) => updateDraftPeriod(slot.period, 'startTime', e.target.value)}
                  className={styles.timeEditorInput}
                />
                <span className={styles.timeEditorDash}>-</span>
                <input
                  type="time"
                  value={slot.endTime}
                  onChange={(e) => updateDraftPeriod(slot.period, 'endTime', e.target.value)}
                  className={styles.timeEditorInput}
                />
              </div>
            ))}

            <div className={styles.modalField}>
              <label>Break label</label>
              <input
                type="text"
                value={scheduleDraft.breakLabel}
                onChange={(e) => setScheduleDraft((prev) => ({ ...prev, breakLabel: e.target.value }))}
              />
            </div>
            <div className={styles.modalField}>
              <label>Break time text</label>
              <input
                type="text"
                value={scheduleDraft.breakTime}
                onChange={(e) => setScheduleDraft((prev) => ({ ...prev, breakTime: e.target.value }))}
              />
            </div>
            <div className={styles.modalField}>
              <label>Lunch label</label>
              <input
                type="text"
                value={scheduleDraft.lunchLabel}
                onChange={(e) => setScheduleDraft((prev) => ({ ...prev, lunchLabel: e.target.value }))}
              />
            </div>
            <div className={styles.modalField}>
              <label>Lunch time text</label>
              <input
                type="text"
                value={scheduleDraft.lunchTime}
                onChange={(e) => setScheduleDraft((prev) => ({ ...prev, lunchTime: e.target.value }))}
              />
            </div>

            <div className={styles.modalActions}>
              <button
                className={`${styles.modalBtn} ${styles.cancel}`}
                type="button"
                onClick={() => {
                  setScheduleDraft(scheduleConfig);
                  setShowScheduleModal(false);
                }}
              >
                Cancel
              </button>
              <button className={`${styles.modalBtn} ${styles.save}`} type="button" onClick={handleSaveSchedule}>
                Save Times
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimetableGridComponent;
